/**
 * createShell — Factory that produces a fully-wired React dashboard app from config.
 *
 * Wires: QueryClientProvider -> BrowserRouter -> ErrorBoundary -> DashboardLayout -> Routes
 *
 * Usage:
 * ```tsx
 * const App = createShell({
 *   name: 'finance',
 *   displayName: 'Finance',
 *   // shortTitle?: 'Fin' -> shown below the `sm` breakpoint instead of
 *   // displayName when the full name would truncate at narrow widths.
 *   sidebarGroups: [...],
 *   defaultRoute: '/finance/overview',
 * });
 *
 * // In main.tsx:
 * ReactDOM.createRoot(root).render(<React.StrictMode><App /></React.StrictMode>);
 * ```
 *
 * @layer layout
 * @package @plerity/dashboard-shell
 */

import type { UnifiedAuthConfig } from "@plerity/auth";
import { AuthGuard, AuthProvider, useAuth } from "@plerity/auth/react";
import type { UserInfo } from "@plerity/dashboard-shared/components";
import { MobileMenuButton, PageSkeleton, ThemeToggle, UserMenu } from "@plerity/dashboard-shared/components";
import { AppShell, DashboardLayout } from "@plerity/dashboard-shared/layouts";
import { usePreferencesStore } from "@plerity/dashboard-shared/stores";
// `trackNavigation` is a tiny, dependency-free nav-tracking helper called
// eagerly on every route change — imported STATICALLY from the deep
// `./captureContext` subpath so it never pulls the 275KB widget (+ its
// html2canvas-pro dep) onto the critical path. The widget itself is
// lazy-loaded below so Rollup can split it into a separate chunk that only
// downloads when a super-admin actually opens the tester-notes FAB.
// (Backlog: dashboards-tester-notes-widget-eager-import-2026-07-13-001.)
import { trackNavigation as trackTesterNotesNavigation } from "@plerity/tester-notes-widget/captureContext";
import { lazy, type ReactNode, Suspense, useCallback, useEffect, useMemo, useRef } from "react";
import { matchPath, Route, Routes, useLocation } from "react-router-dom";
import { DevNotesProvider } from "./components/DevNotesProvider";
import { isDevNotesEnabled } from "./config/feature-flags";
import { PORTAL_URLS } from "./config/portal-urls";
import { NotFoundPage } from "./NotFoundPage";
import { ShellErrorBoundary } from "./ShellErrorBoundary";
import { buildShellRoutes } from "./ShellRouter";
import { useForeignModalOpen } from "./shellFab";
import type { RouteModule, ShellConfig } from "./types";
import { routesToNavSections, sidebarGroupsToNavSections } from "./types";

/**
 * Lazy-loaded tester-notes widget. Because `testerNotes.enabled` is a RUNTIME
 * config flag (not a build-time constant), Rollup cannot tree-shake a static
 * import even where the widget never renders — so a static import ships the
 * 275KB widget + html2canvas-pro eagerly on the critical path of EVERY shell.
 * Loading it via `lazy()` moves it into its own dynamic chunk that only
 * downloads when a super-admin opens the FAB. Mirrors the route-level
 * lazy-loading in ShellRouter.tsx.
 */
const TesterNotesWidget = lazy(() =>
  import("@plerity/tester-notes-widget").then((m) => ({
    default: m.TesterNotesWidget,
  }))
);

/**
 * Lazy-loaded settings route. The hidden `/settings` route renders the shared
 * `UserSettingsPage` (+ Appearance / Notifications / Profile / Privacy /
 * Assistant / Tester-Notes tabs, ~31 KB raw). A static import put all of it in
 * every shell's eager `shared` chunk even though only the avatar-menu → Settings
 * navigation ever renders it. It now lands in its own lazy chunk, fetched on
 * first visit to the settings route. (Backlog: perf-2026-10-12-004. Lazy-loading
 * alone was not enough — the settings widgets are also off the
 * `@plerity/dashboard-shared` barrels and excluded from the `shared` manual chunk.)
 */
const SettingsRoute = lazy(() => import("./SettingsRoute").then((m) => ({ default: m.SettingsRoute })));

/**
 * Lazy-loaded chat assistant widget. Same reasoning as TesterNotesWidget:
 * `chat.enabled` is a RUNTIME flag, so a static import kept the chat-widget
 * chunk (~22.5 KB raw / 7.4 KB gzip) in EVERY shell's first-load
 * modulepreload set. The bubble now renders after one chunk fetch.
 * (Backlog: perf-2026-10-12-001.)
 */
const ChatWidget = lazy(() =>
  import("@plerity/chat-widget").then((m) => ({
    default: m.ChatWidget,
  }))
);

/**
 * ShellFloatingWidget — Renders whichever bottom-right widget the user
 * selected via Settings → Floating widget.
 *
 * Three options:
 *  - `assistant`     → `<ChatWidget>` (when `chat.enabled`)
 *  - `tester-notes`  → `<TesterNotesWidget>` (when `testerNotes.enabled`
 *                       AND user is super-admin OR director)
 *  - `none`          → renders nothing
 *
 * If the user picks an option that the current app doesn't ship (e.g. a
 * shell without a chat config), this renders nothing — the setting acts
 * as a no-op until that surface gets the matching wiring. We deliberately
 * do NOT auto-fall-back to the other widget because that would
 * unexpectedly show the chat to a user who explicitly chose tester-notes.
 *
 * Role gate (added 2026-05-05 — tester_notes_shared_infrastructure_completion_plan;
 * simplified 2026-05-21 — admin_level retirement):
 * The tester-notes FAB is intended for internal QA/triage use only and is
 * gated to `isSuperAdmin === true`. Member-level employees and external
 * client users never see the FAB even if the preference is set. Director
 * tier gating was retired alongside the `admin_level` JWT field; if
 * director-specific access is needed later, gate on a capability key
 * surfaced through `useAuth()`. The chat assistant has no such restriction.
 *
 * Mounted INSIDE the AppShell BrowserRouter — uses `useLocation()` to
 * pass the current route into the widgets for context.
 */
function ShellFloatingWidget({
  shellName,
  displayName,
  chat,
  testerNotes,
  authConfigured,
}: {
  shellName: string;
  displayName: string;
  chat?: ShellConfig["chat"];
  testerNotes?: ShellConfig["testerNotes"];
  /**
   * True when `createShell({ auth: ... })` is configured — meaning
   * `<AuthProvider>` is mounted as an ancestor and `useAuth()` is safe to
   * call. When false (rare; no-auth dev shells / unit harnesses), the
   * tester-notes FAB falls back to the legacy "show for everyone with
   * the preference" behaviour to avoid breaking pre-auth shells.
   */
  authConfigured: boolean;
}) {
  const location = useLocation();
  const widget = usePreferencesStore((s) => s.bottomRightWidget);

  // Track nav for tester-notes "prior_page_path" capture, regardless of
  // which widget is currently shown — keeps the breadcrumb accurate if
  // the user toggles between modes mid-session.
  useEffect(() => {
    trackTesterNotesNavigation(location.pathname);
  }, [location.pathname]);

  // 1) Assistant chat
  if (widget === "assistant" && chat?.enabled) {
    const chatApiEndpoint =
      chat.apiEndpoint || (import.meta.env.DEV ? "http://localhost:8370/api/chat" : "/api/_assistant/chat");
    return (
      <Suspense fallback={null}>
        <ChatWidget
          appName={shellName}
          appDisplayName={displayName}
          currentRoute={location.pathname}
          features={chat.features}
          extraContext={chat.systemContext}
          apiEndpoint={chatApiEndpoint}
          toggleShortcut={chat.toggleShortcut}
          shortcutHint={chat.shortcutHint}
        />
      </Suspense>
    );
  }

  // 2) Tester notes — gated on isSuperAdmin (admin_level retirement, 2026-05-21).
  if (widget === "tester-notes" && testerNotes?.enabled) {
    return (
      <TesterNotesGate
        authConfigured={authConfigured}
        contextId={testerNotes.contextId || `web-${shellName}`}
        appDisplayName={displayName}
        currentRoute={location.pathname}
        apiEndpoint={
          testerNotes.apiEndpoint || (import.meta.env.DEV ? "http://localhost:8395/api/dev-notes" : "/api/dev-notes")
        }
      />
    );
  }

  // 3) Either user picked `none` OR picked an option the app doesn't ship.
  return null;
}

/**
 * TesterNotesGate — Reads auth via `useAuth()` and only renders the
 * widget when the user is a director or super-admin. Pulled into a
 * separate component so the parent `ShellFloatingWidget` can stay
 * usable in non-auth shells (rare; the gate falls open in that case).
 */
function TesterNotesGate({
  authConfigured,
  contextId,
  appDisplayName,
  currentRoute,
  apiEndpoint,
}: {
  authConfigured: boolean;
  contextId: string;
  appDisplayName: string;
  currentRoute: string;
  apiEndpoint: string;
}) {
  // When auth isn't configured for this shell, fall back to the legacy
  // behaviour (no role gate). This protects unit harnesses and pre-auth
  // dev shells from breaking.
  if (!authConfigured) {
    return (
      <Suspense fallback={null}>
        <TesterNotesWidget
          contextId={contextId}
          appDisplayName={appDisplayName}
          currentRoute={currentRoute}
          apiEndpoint={apiEndpoint}
        />
      </Suspense>
    );
  }
  return (
    <TesterNotesAuthGate
      contextId={contextId}
      appDisplayName={appDisplayName}
      currentRoute={currentRoute}
      apiEndpoint={apiEndpoint}
    />
  );
}

/**
 * TesterNotesAuthGate — separate component (called only when AuthProvider
 * is mounted) so `useAuth()` is safe. Renders the widget only when the
 * current user is a super-admin (2026-05-21: director-tier admin_level
 * gate retired alongside the admin_level JWT field).
 */
function TesterNotesAuthGate({
  contextId,
  appDisplayName,
  currentRoute,
  apiEndpoint,
}: {
  contextId: string;
  appDisplayName: string;
  currentRoute: string;
  apiEndpoint: string;
}) {
  const { user } = useAuth();
  if (user?.isSuperAdmin !== true) return null;
  return (
    <Suspense fallback={null}>
      <TesterNotesWidget
        contextId={contextId}
        appDisplayName={appDisplayName}
        currentRoute={currentRoute}
        apiEndpoint={apiEndpoint}
      />
    </Suspense>
  );
}

/** Collect all RouteModules from either sidebarGroups or flat routes */
function collectRoutes(config: ShellConfig): RouteModule[] {
  if (config.sidebarGroups) {
    return config.sidebarGroups.flatMap((group) => group.routes);
  }
  return config.routes ?? [];
}

/** Build navigation sections from config */
function buildNavSections(config: ShellConfig) {
  if (config.sidebarGroups) {
    return sidebarGroupsToNavSections(config.sidebarGroups);
  }
  if (config.routes) {
    return routesToNavSections(config.routes, config.displayName);
  }
  return [];
}

/** Per-route chrome rule derived from `RouteModule.fullBleed` / `hideChrome`. */
export interface RouteChromeRule {
  pattern: string;
  fullBleed: boolean;
  hideChrome: boolean;
}

export interface RouteChrome {
  fullBleed: boolean;
  hideChrome: boolean;
}

const DEFAULT_CHROME: RouteChrome = { fullBleed: false, hideChrome: false };

/**
 * Flatten routes (incl. children — they are absolute paths in the
 * flat-sibling model, see ShellRouter.tsx) into chrome rules. Only routes that
 * opt in are kept, so the common case is an empty list and a zero-cost lookup.
 */
export function collectRouteChromeRules(routes: RouteModule[]): RouteChromeRule[] {
  const rules: RouteChromeRule[] = [];
  const walk = (list: RouteModule[]) => {
    for (const r of list) {
      // An explicit `hideChrome: false` / `fullBleed: false` is kept too: it is the
      // only way a STATIC sibling (`/video/quick-reel`) can keep the chrome when a
      // dynamic sibling (`/video/:id`) opts out — `:id` also matches "quick-reel".
      const declared = r.fullBleed !== undefined || r.hideChrome !== undefined;
      if (declared && !r.external) {
        rules.push({
          pattern: r.path.startsWith("/") ? r.path : `/${r.path}`,
          fullBleed: Boolean(r.fullBleed || r.hideChrome),
          hideChrome: Boolean(r.hideChrome),
        });
      }
      if (r.children) walk(r.children);
    }
  };
  walk(routes);
  // Static patterns before dynamic ones (stable), so `/video/quick-reel` wins over `/video/:id`.
  const isDynamic = (pattern: string) => /[:*]/.test(pattern);
  return rules
    .map((rule, i) => ({ rule, i }))
    .sort((a, b) => Number(isDynamic(a.rule.pattern)) - Number(isDynamic(b.rule.pattern)) || a.i - b.i)
    .map(({ rule }) => rule);
}

/** Resolve the chrome mode for a (basename-stripped) pathname. First match wins (static before dynamic). */
export function resolveRouteChrome(pathname: string, rules: readonly RouteChromeRule[]): RouteChrome {
  for (const rule of rules) {
    if (matchPath({ path: rule.pattern, end: true }, pathname)) {
      return { fullBleed: rule.fullBleed, hideChrome: rule.hideChrome };
    }
  }
  return DEFAULT_CHROME;
}

/**
 * Create a fully-wired dashboard shell app from configuration.
 *
 * Returns a React component that renders:
 * - AppShell (QueryClient + BrowserRouter)
 * - ShellErrorBoundary (included — do NOT wrap the result in another ErrorBoundary)
 * - DashboardLayout (sidebar + header + outlet)
 * - Auto-generated routes with lazy loading
 */
/**
 * Decide whether the current location is a "public" path that should bypass
 * AuthGuard even when `auth.required: true`.
 *
 * Matches when the pathname is EXACTLY one of `publicPaths` OR begins with
 * `<entry>/` (so trailing-slash and nested-route variants both match). The
 * empty / undefined / empty-array cases all return `false` (every route is
 * gated).
 *
 * Exported (un-prefixed name kept simple) for the dashboard-shell unit
 * tests; consumers should not import this directly — use the
 * `auth.publicPaths` config field on createShell() instead.
 */
export function isPublicShellPath(pathname: string, publicPaths: readonly string[] | undefined): boolean {
  if (!publicPaths || publicPaths.length === 0) return false;
  return publicPaths.some((entry) => pathname === entry || pathname.startsWith(`${entry}/`));
}

/**
 * Conditional AuthGuard — applies AuthGuard only when the current pathname
 * is NOT in the `publicPaths` list. Public paths render children directly
 * (still inside AuthProvider, so `useAuth()` works for opt-in personalisation).
 *
 * Must be rendered BELOW AppShell (BrowserRouter) because it depends on
 * `useLocation`. See createShell() callsite below.
 */
function ConditionalAuthGuard({
  publicPaths,
  entitlement,
  loginUrl,
  children,
}: {
  publicPaths: readonly string[] | undefined;
  entitlement?: string | string[];
  loginUrl: string;
  children: ReactNode;
}) {
  const location = useLocation();
  if (isPublicShellPath(location.pathname, publicPaths)) {
    return <>{children}</>;
  }
  return (
    <AuthGuard entitlement={entitlement} loginUrl={loginUrl}>
      {children}
    </AuthGuard>
  );
}

/** Bridge component: derives UserInfo from auth state for the UserMenu */
function AuthUserMenuBridge({
  settingsRoute,
  securityRoute,
  headerActions,
  toggleSidebar,
}: {
  settingsRoute?: string;
  securityRoute?: string;
  headerActions?: React.ReactNode;
  toggleSidebar: () => void;
}) {
  const { user, signOut } = useAuth();

  const userInfo: UserInfo | undefined = useMemo(() => {
    if (!user) return undefined;
    return {
      name: user.name,
      email: user.email,
      avatarUrl: undefined,
    };
  }, [user]);

  return (
    <>
      {headerActions}
      {userInfo ? (
        <UserMenu user={userInfo} settingsRoute={settingsRoute} securityRoute={securityRoute} onSignOut={signOut} />
      ) : (
        <ThemeToggle />
      )}
      <MobileMenuButton onClick={toggleSidebar} />
    </>
  );
}

export function createShell(config: ShellConfig): React.FC {
  const {
    name,
    displayName,
    shortTitle,
    defaultRoute,
    headerActions,
    brand,
    apps,
    currentApp,
    basename,
    notFoundComponent: NotFound = NotFoundPage,
    sidebarFooter,
    chat,
    testerNotes,
    user,
    settingsRoute,
    securityRoute,
    onSignOut,
    auth,
    settings,
    router: routerMode = "browser",
  } = config;

  const navigation = buildNavSections(config);
  const allRoutes = collectRoutes(config);
  const chromeRules = collectRouteChromeRules(allRoutes);
  const useRouteChrome = (): RouteChrome => {
    const { pathname } = useLocation();
    return chromeRules.length === 0 ? DEFAULT_CHROME : resolveRouteChrome(pathname, chromeRules);
  };

  /**
   * Layout route element — resolves per-route chrome (fullBleed/hideChrome)
   * and FAB clearance, then renders the shared DashboardLayout.
   */
  const ShellLayout = (props: { headerActions: ReactNode; sidebarCollapsed: boolean; onToggleSidebar: () => void }) => {
    const chrome = useRouteChrome();
    const widget = usePreferencesStore((s) => s.bottomRightWidget);
    const fabEnabled =
      (widget === "assistant" && Boolean(chat?.enabled)) ||
      (widget === "tester-notes" && Boolean(testerNotes?.enabled));
    return (
      <DashboardLayout
        title={displayName}
        shortTitle={shortTitle}
        navigation={navigation}
        headerActions={props.headerActions}
        sidebarCollapsed={props.sidebarCollapsed}
        onToggleSidebar={props.onToggleSidebar}
        brand={brand}
        apps={apps}
        currentApp={currentApp ?? name}
        sidebarFooter={sidebarFooter}
        fullBleed={chrome.fullBleed}
        hideChrome={chrome.hideChrome}
        reserveFabSpace={fabEnabled}
      />
    );
  };

  /**
   * Floating widget, suppressed on immersive (hideChrome) routes and HIDDEN
   * (not unmounted — keeps chat state) while a route's own aria-modal dialog
   * or drawer is open: the FAB shares the fleet modal layer (z-50) and, being
   * later in the DOM, would otherwise float over the dialog (see shellFab.ts).
   */
  const ShellFab = () => {
    const chrome = useRouteChrome();
    const rootRef = useRef<HTMLDivElement>(null);
    const foreignModalOpen = useForeignModalOpen(rootRef);
    if (chrome.hideChrome) return null;
    return (
      <div
        ref={rootRef}
        data-shell-fab-root=""
        data-hidden-by-modal={foreignModalOpen ? "" : undefined}
        // Inline display (not Tailwind `hidden`/`contents`) so the toggle can
        // never be purged by an app's Tailwind source scan. `contents` keeps
        // the wrapper out of layout; the bubbles are position:fixed anyway.
        style={{ display: foreignModalOpen ? "none" : "contents" }}
      >
        <ShellFloatingWidget
          shellName={name}
          displayName={displayName}
          chat={chat}
          testerNotes={testerNotes}
          authConfigured={Boolean(auth)}
        />
      </div>
    );
  };

  const Shell: React.FC = () => {
    const sidebarCollapsed = usePreferencesStore((s) => s.sidebarCollapsed);
    const setSidebarCollapsed = usePreferencesStore((s) => s.setSidebarCollapsed);

    const toggleSidebar = useCallback(
      () => setSidebarCollapsed(!sidebarCollapsed),
      [sidebarCollapsed, setSidebarCollapsed]
    );

    // Auto-collapse sidebar on small screens (initial check + resize)
    useEffect(() => {
      const mq = window.matchMedia("(max-width: 1023px)");
      // Check on initial mount
      if (mq.matches) setSidebarCollapsed(true);
      const handler = (e: MediaQueryListEvent) => setSidebarCollapsed(e.matches);
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }, [setSidebarCollapsed]);

    // When `auth` config is provided, use AuthUserMenuBridge (reads from AuthProvider).
    // Otherwise, use the manual `user` prop for backward compat.
    const resolvedHeaderActions = auth ? (
      <AuthUserMenuBridge
        settingsRoute={settingsRoute}
        securityRoute={securityRoute}
        headerActions={headerActions}
        toggleSidebar={toggleSidebar}
      />
    ) : headerActions !== undefined ? (
      <>
        {headerActions}
        {user && (
          <UserMenu user={user} settingsRoute={settingsRoute} securityRoute={securityRoute} onSignOut={onSignOut} />
        )}
        {!user && <ThemeToggle />}
        <MobileMenuButton onClick={toggleSidebar} />
      </>
    ) : (
      <>
        {user ? (
          <UserMenu user={user} settingsRoute={settingsRoute} securityRoute={securityRoute} onSignOut={onSignOut} />
        ) : (
          <ThemeToggle />
        )}
        <MobileMenuButton onClick={toggleSidebar} />
      </>
    );

    // Inner shell content — must be rendered BELOW the Router (AppShell)
    // because ShellChatWidget + ConditionalAuthGuard both depend on
    // `useLocation`. Extracted so AuthGuard can be applied conditionally
    // by-path while still keeping AppShell at the top of the tree.
    const innerContent = (
      <ShellErrorBoundary shellName={name}>
        <Routes>
          <Route
            element={
              <ShellLayout
                headerActions={resolvedHeaderActions}
                sidebarCollapsed={sidebarCollapsed}
                onToggleSidebar={toggleSidebar}
              />
            }
          >
            {/*
              When `config.settings` is provided, register a hidden settings
              route that renders the shared <UserSettingsPage>. We inject it
              as a sibling Route here (rather than as a sidebar entry) so the
              user navigates via the UserMenu's settings link without
              cluttering the nav. Route path defaults to `/settings` —
              consumers can override via `config.settingsRoute`.
            */}
            {settings && (
              <Route
                path={(settingsRoute ?? "/settings").replace(/^\//, "")}
                element={
                  <Suspense fallback={<PageSkeleton />}>
                    <SettingsRoute
                      useAuthBridge={Boolean(auth)}
                      fallbackUser={user}
                      additionalTabs={settings.additionalTabs}
                      /*
                       * Auto-derive the Tester Notes contextId from the
                       * sibling `testerNotes` block so the two can never
                       * drift. Apps can still override explicitly via
                       * `settings.testerNotesContextId` if needed (e.g.
                       * super-admin viewing another shell's notes).
                       */
                      testerNotesContextId={settings.testerNotesContextId ?? testerNotes?.contextId ?? `web-${name}`}
                      assistantName={settings.assistantName ?? `${displayName} Assistant`}
                      disableBaseTabs={settings.disableBaseTabs}
                    />
                  </Suspense>
                }
              />
            )}
            {buildShellRoutes({
              routes: allRoutes,
              defaultRoute,
              notFoundElement: <NotFound />,
            })}
          </Route>
        </Routes>
        {/*
          Bottom-right floating widget — picks Assistant / Tester Notes /
          None based on the user's preference (Settings → Floating widget).
          Always rendered so the toggle is live across the SPA, but only
          actually paints DOM when the chosen widget is enabled for this
          shell AND the user hasn't picked "none".

          FAB safe-area: the 56×56 FAB is `position: fixed` bottom-right and
          can cosmetically overlap a hosted route's own fixed-bottom composer
          at narrow widths (measured on Ops /comms/rooms/:id at 375px —
          backlog shell-fab-composer-overlap-2026-08-29-001). A hosted route
          that renders such a composer can lift the FAB clear of it by setting
          the `--shell-fab-safe-area` CSS var (e.g. `72px`) on a shell-level
          ancestor — `document.documentElement` (`:root`) is simplest since
          the FAB is a sibling of the route outlet, not a descendant. Both
          `<ChatBubble>` and `<TesterNotesBubble>` read it; it defaults to
          `0px`, so routes that don't opt in are unaffected.
        */}
        <ShellFab />
        {/*
          Legacy DevNotes annotation system (right-click any element →
          add note). Independent of the new Tester Notes capture FAB —
          retained behind the existing URL-flag/env-flag for marketing-
          site usage and any in-flight users. Will be retired after the
          Tester Notes rollout settles.
        */}
        {isDevNotesEnabled() && <DevNotesProvider contextId={name} contextType="app" />}
      </ShellErrorBoundary>
    );

    // When auth is configured, wrap in AuthProvider + (conditional) AuthGuard.
    //
    // 2026-04-29: AuthGuard now lives INSIDE AppShell (BrowserRouter) so
    // `ConditionalAuthGuard` can read `useLocation()` and bypass the gate
    // for paths declared in `auth.publicPaths` (e.g. /dgo/onboarding —
    // signup-flow entry point whose users do not yet have a portal account).
    if (auth) {
      const resolvedLoginUrl = auth.loginUrl ?? PORTAL_URLS.LOGIN;
      const authConfig: UnifiedAuthConfig = {
        loginUrl: resolvedLoginUrl,
        logoutUrl: auth.logoutUrl ?? PORTAL_URLS.LOGOUT,
        entitlement: auth.entitlement,
        portalApiUrl: auth.portalApiUrl ?? PORTAL_URLS.API,
      };

      return (
        <AuthProvider config={authConfig}>
          <AppShell basename={basename} router={routerMode}>
            {auth.required ? (
              <ConditionalAuthGuard
                publicPaths={auth.publicPaths}
                entitlement={auth.entitlement}
                loginUrl={resolvedLoginUrl}
              >
                {innerContent}
              </ConditionalAuthGuard>
            ) : (
              innerContent
            )}
          </AppShell>
        </AuthProvider>
      );
    }

    return (
      <AppShell basename={basename} router={routerMode}>
        {innerContent}
      </AppShell>
    );
  };

  Shell.displayName = `Shell(${name})`;
  return Shell;
}
